import React from 'react';
import { Card } from '../../components/ui/card';
import { TrendingUp, AlertTriangle } from 'lucide-react';

/* CRM report bodies: the five guest-growth reports as standalone routes, each given data and formatters. */

const SEGMENT_COLORS = {
  'Champions': 'bg-emerald-100 text-emerald-800',
  'Loyal': 'bg-green-100 text-green-700',
  'Big Spenders': 'bg-violet-100 text-violet-700',
  'Promising': 'bg-sky-100 text-sky-700',
  'Needs Attention': 'bg-amber-100 text-amber-700',
  'At Risk': 'bg-orange-100 text-orange-700',
  'Lost': 'bg-gray-200 text-gray-600',
};

const riskBadge = (risk) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
    risk === 'HIGH' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
  }`}>{risk}</span>
);

export function RfmBody({ data, money, openProfile }) {
  const rfmMaxRevenue = Math.max(1, ...((data.segments || []).map((s) => s.revenue)));
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-1">Guest segments (Recency · Frequency · Monetary)</h3>
        <p className="text-sm text-gray-500 mb-4">
          Guests scored on recency, frequency and spend, then grouped.
        </p>
        <div className="space-y-3">
          {data.segments.map((s) => (
            <div key={s.segment} className="rounded-lg border border-gray-100 p-3">
              <div className="flex flex-wrap items-center gap-3 mb-1.5">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${SEGMENT_COLORS[s.segment] || 'bg-gray-100 text-gray-600'}`}>
                  {s.segment}
                </span>
                <span className="text-sm text-gray-600">{s.customers} guest{s.customers === 1 ? '' : 's'}</span>
                <span className="text-sm font-semibold text-gray-900 ml-auto">{money(s.revenue)}</span>
              </div>
              <div className="h-1.5 rounded bg-gray-100 mb-2">
                <div className="h-1.5 rounded bg-amber-500"
                  style={{ width: `${Math.max(2, (s.revenue / rfmMaxRevenue) * 100)}%` }} />
              </div>
              <p className="text-xs text-gray-500">{s.action}</p>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-3">All scored guests</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left font-medium py-2 pr-4">Guest</th>
                <th className="text-left font-medium py-2 pr-4">Segment</th>
                <th className="text-center font-medium py-2 pr-4">R·F·M</th>
                <th className="text-right font-medium py-2 pr-4">Visits</th>
                <th className="text-right font-medium py-2 pr-4">Spend</th>
                <th className="text-right font-medium py-2">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.customers.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openProfile(c.id)}>
                  <td className="py-2 pr-4">
                    <div className="font-medium text-gray-900">{c.name || '—'}</div>
                    <div className="text-xs text-gray-500">{c.phone}</div>
                  </td>
                  <td className="py-2 pr-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${SEGMENT_COLORS[c.segment] || 'bg-gray-100 text-gray-600'}`}>
                      {c.segment}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-center font-mono text-xs">{c.r}·{c.f}·{c.m}</td>
                  <td className="py-2 pr-4 text-right">{c.visits}</td>
                  <td className="py-2 pr-4 text-right">{money(c.total_spend)}</td>
                  <td className="py-2 text-right">{c.days_since_last_visit}d ago</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export function TopCustomersBody({ data, money, dt, openProfile }) {
  return (
    <Card className="p-5">
      <h3 className="font-semibold text-gray-800 mb-1">Top customers by lifetime value</h3>
      <p className="text-sm text-gray-500 mb-4">
        Who funds the restaurant. Projected annual extrapolates monthly spend.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-gray-500">
            <tr>
              <th className="text-left font-medium py-2 pr-4">#</th>
              <th className="text-left font-medium py-2 pr-4">Guest</th>
              <th className="text-right font-medium py-2 pr-4">Visits</th>
              <th className="text-right font-medium py-2 pr-4">Total spend</th>
              <th className="text-right font-medium py-2 pr-4">Avg order</th>
              <th className="text-right font-medium py-2 pr-4">Spend / month</th>
              <th className="text-right font-medium py-2 pr-4">Projected annual</th>
              <th className="text-left font-medium py-2 pr-4">Favourite</th>
              <th className="text-right font-medium py-2">Last visit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.customers.map((c, i) => (
              <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openProfile(c.id)}>
                <td className="py-2 pr-4 text-gray-400">{i + 1}</td>
                <td className="py-2 pr-4">
                  <div className="font-medium text-gray-900">{c.name || '—'}</div>
                  <div className="text-xs text-gray-500">{c.phone}</div>
                </td>
                <td className="py-2 pr-4 text-right">{c.visits}</td>
                <td className="py-2 pr-4 text-right font-semibold">{money(c.total_spend)}</td>
                <td className="py-2 pr-4 text-right">{money(c.avg_order_value)}</td>
                <td className="py-2 pr-4 text-right">{money(c.spend_per_month)}</td>
                <td className="py-2 pr-4 text-right text-emerald-700 font-medium">
                  <TrendingUp size={13} className="inline mr-1" />{money(c.projected_annual_value)}
                </td>
                <td className="py-2 pr-4">{c.favourite_item || '—'}</td>
                <td className="py-2 text-right">{dt(c.last_visit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function RetentionBody({ data, money }) {
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            ['Guests with orders', data.overall.customers_with_orders],
            ['Came back 2+ times', data.overall.repeat_customers],
            ['Overall repeat rate', `${data.overall.repeat_rate_pct}%`],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg bg-gray-50 p-4">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="text-2xl font-bold text-gray-900">{value}</div>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-3">
          Winning a guest back costs far less than finding a new one.
        </p>
      </Card>
      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-3">New vs returning guests by month</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left font-medium py-2 pr-4">Month</th>
                <th className="text-right font-medium py-2 pr-4">New</th>
                <th className="text-right font-medium py-2 pr-4">Returning</th>
                <th className="text-right font-medium py-2 pr-4">Repeat %</th>
                <th className="text-right font-medium py-2 pr-4">Orders</th>
                <th className="text-right font-medium py-2 pr-4">Revenue</th>
                <th className="text-left font-medium py-2">Mix</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.months.map((m) => (
                <tr key={m.month}>
                  <td className="py-2 pr-4 font-medium text-gray-900">{m.month}</td>
                  <td className="py-2 pr-4 text-right text-sky-700">{m.new_customers}</td>
                  <td className="py-2 pr-4 text-right text-emerald-700">{m.returning_customers}</td>
                  <td className="py-2 pr-4 text-right font-semibold">{m.repeat_rate_pct}%</td>
                  <td className="py-2 pr-4 text-right">{m.orders}</td>
                  <td className="py-2 pr-4 text-right">{money(m.revenue)}</td>
                  <td className="py-2 w-40">
                    <div className="flex h-2 rounded overflow-hidden bg-gray-100 min-w-[8rem]">
                      <div className="bg-sky-400" style={{ width: `${m.total_customers ? (m.new_customers / m.total_customers) * 100 : 0}%` }} />
                      <div className="bg-emerald-500" style={{ width: `${m.total_customers ? (m.returning_customers / m.total_customers) * 100 : 0}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-400 mt-2">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-sky-400 mr-1" />New ·
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500 mx-1" />Returning
          </p>
        </div>
      </Card>
    </div>
  );
}

export function AffinityBody({ data, money }) {
  return (
    <Card className="p-5">
      <h3 className="font-semibold text-gray-800 mb-1">Menu magnets — dishes that bring guests back</h3>
      <p className="text-sm text-gray-500 mb-4">
        Ranked by distinct guests. Repeat % is those who ordered it again.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-gray-500">
            <tr>
              <th className="text-left font-medium py-2 pr-4">Dish</th>
              <th className="text-right font-medium py-2 pr-4">Guests</th>
              <th className="text-right font-medium py-2 pr-4">Orders</th>
              <th className="text-right font-medium py-2 pr-4">Qty sold</th>
              <th className="text-right font-medium py-2 pr-4">Revenue</th>
              <th className="text-right font-medium py-2 pr-4">Repeat guests</th>
              <th className="text-right font-medium py-2">Repeat %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.items.map((r) => (
              <tr key={r.item}>
                <td className="py-2 pr-4 font-medium text-gray-900">{r.item}</td>
                <td className="py-2 pr-4 text-right">{r.distinct_customers}</td>
                <td className="py-2 pr-4 text-right">{r.orders}</td>
                <td className="py-2 pr-4 text-right">{r.quantity}</td>
                <td className="py-2 pr-4 text-right">{money(r.revenue)}</td>
                <td className="py-2 pr-4 text-right">{r.repeat_customers}</td>
                <td className="py-2 text-right">
                  <span className={`font-semibold ${r.repeat_rate_pct >= 30 ? 'text-emerald-700' : 'text-gray-700'}`}>
                    {r.repeat_rate_pct}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function ChurnBody({ data, money, dt, openProfile }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="font-semibold text-gray-800">Win-back list — guests going quiet</h3>
        <span className="inline-flex items-center gap-1 text-sm text-amber-700">
          <AlertTriangle size={15} /> {data.total_at_risk} at risk
        </span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Regulars past their own visiting rhythm. 3× past is high risk.
      </p>
      {data.customers.length === 0 ? (
        <p className="text-sm text-gray-500">No one is overdue — your regulars are on rhythm. 🎉</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left font-medium py-2 pr-4">Guest</th>
                <th className="text-left font-medium py-2 pr-4">Risk</th>
                <th className="text-right font-medium py-2 pr-4">Last visit</th>
                <th className="text-right font-medium py-2 pr-4">Days silent</th>
                <th className="text-right font-medium py-2 pr-4">Usual gap</th>
                <th className="text-right font-medium py-2 pr-4">Visits</th>
                <th className="text-right font-medium py-2 pr-4">Lifetime spend</th>
                <th className="text-right font-medium py-2">Value / visit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.customers.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openProfile(c.id)}>
                  <td className="py-2 pr-4">
                    <div className="font-medium text-gray-900">{c.name || '—'}</div>
                    <div className="text-xs text-gray-500">{c.phone}</div>
                  </td>
                  <td className="py-2 pr-4">{riskBadge(c.risk)}</td>
                  <td className="py-2 pr-4 text-right">{dt(c.last_visit)}</td>
                  <td className="py-2 pr-4 text-right font-semibold">{c.days_since_last_visit}</td>
                  <td className="py-2 pr-4 text-right">~{c.usual_gap_days}d</td>
                  <td className="py-2 pr-4 text-right">{c.visits}</td>
                  <td className="py-2 pr-4 text-right">{money(c.total_spend)}</td>
                  <td className="py-2 text-right">{money(c.value_at_risk_per_visit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// key → body component, consumed by ReportView's ReportBody switch.
export const CRM_REPORT_BODIES = {
  'crm-segments': RfmBody,
  'crm-top-customers': TopCustomersBody,
  'crm-retention': RetentionBody,
  'crm-menu-magnets': AffinityBody,
  'crm-winback': ChurnBody,
};
